/**
 * AdminMarketingPage — oversight for the automated email engine.
 *
 * Panels:
 *  1. Overview: deliverability & sends (24h / 7d), contact counts
 *  2. Sequences: list all sequences, toggle active/paused
 *  3. Templates: preview any email + send a test to your own inbox
 *  4. Contacts: paginated search
 *
 * Nothing here creates enrollments — imports are done by CLI so a full
 * consent/sign-off gate stays in place. This UI is *oversight*, not authoring.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Users, Play, Pause, Eye, Send, RefreshCw, ChevronLeft, ChevronRight, ShieldCheck, TrendingUp, Upload, Pencil, RotateCcw, History, Save, AlertTriangle, Check } from "lucide-react";

const AdminMarketingPage = () => {
  const [tab, setTab] = useState("overview");
  return (
    <div data-testid="admin-marketing-page" className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Email marketing</h1>
        <p className="text-sm text-muted-foreground">
          Automated lifecycle emails. Contacts, sequences, and deliverability in one place.
          Every sequence is <strong>off by default</strong> — flip it on when copy is signed off.
        </p>
      </header>

      <WorkerStaleBanner />

      <div className="flex gap-2 border-b border-border overflow-x-auto">
        {[
          { k: "overview", label: "Overview" },
          { k: "performance", label: "Performance" },
          { k: "sequences", label: "Sequences" },
          { k: "sendlog", label: "Sequence log" },
          { k: "trial", label: "Trial Emails" },
          { k: "templates", label: "Templates" },
          { k: "contacts", label: "Contacts" },
          { k: "event", label: "Mock event" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`mkt-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              tab === t.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewPanel />}
      {tab === "performance" && <PerformancePanel />}
      {tab === "sequences" && <SequencesPanel />}
      {tab === "sendlog" && <SequenceLogPanel />}
      {tab === "trial" && <TrialEmailsPanel />}
      {tab === "templates" && <TemplatesPanel />}
      {tab === "contacts" && <ContactsPanel />}
      {tab === "event" && <MockEventPanel />}
    </div>
  );
};

// -------------------------- OVERVIEW --------------------------------------
const OverviewPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get("/admin/marketing/overview")); }
    catch { toast.error("Failed to load overview"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const c = data.contacts, s = data.sends;
  const StatCard = ({ label, value, hint, testid }) => (
    <div data-testid={testid} className="rounded-xl border border-border p-4 bg-card">
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-5">
      <LaunchReadinessPanel />
      <WebhookDiagnosticsPanel />
      <BroadcastsPanel />
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button></div>
      <div>
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">Contacts</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard testid="mkt-stat-total" label="Total" value={c.total} />
          <StatCard testid="mkt-stat-active" label="Active" value={c.active} hint="Can receive marketing" />
          <StatCard testid="mkt-stat-unsub" label="Unsubscribed" value={c.unsubscribed} />
          <StatCard testid="mkt-stat-bounced" label="Bounced" value={c.bounced} />
          <StatCard testid="mkt-stat-complained" label="Complained" value={c.complained} />
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">Deliverability (last 7 days)</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard testid="mkt-stat-sent24" label="Sent 24h" value={s.last_24h} />
          <StatCard testid="mkt-stat-sent7" label="Sent 7d" value={s.last_7d} />
          <StatCard testid="mkt-stat-open-rate" label="Open rate" value={`${s.open_rate_pct}%`} hint="Industry avg ~22%" />
          <StatCard testid="mkt-stat-click-rate" label="Click rate" value={`${s.click_rate_pct}%`} hint="Industry avg ~2.5%" />
          <StatCard testid="mkt-stat-complaint-rate" label="Complaints" value={`${s.complaint_rate_pct}%`} hint="Auto-pause at 0.3%" />
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">Active enrollments</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard testid="mkt-stat-enrollments" label="Contacts in an active sequence" value={data.enrollments_active} />
        </div>
      </div>
    </div>
  );
};

// -------------------------- BROADCASTS -----------------------------------
const BroadcastsPanel = () => {
  const [audiences, setAudiences] = useState({ trialing: null, free: null, trial_and_free: null });
  const [sending, setSending] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    const next = {};
    for (const seg of ["trialing", "free", "trial_and_free"]) {
      try { next[seg] = await api.get(`/admin/marketing/broadcast/audience?segment=${seg}`); }
      catch { next[seg] = null; }
    }
    setAudiences(next);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doSend = async (kind, segment, dry_run = false) => {
    const audience = audiences[segment];
    const label = kind === "feedback_request" ? "founder feedback request" : "new-price announcement";
    if (!dry_run) {
      const ok = window.confirm(
        `Send the ${label} to ${audience?.count ?? "?"} ${segment} users?\n\n` +
        `Recipients who received this same broadcast in the last 24h will be skipped automatically.`
      );
      if (!ok) return;
    }
    setSending(`${kind}:${segment}`);
    try {
      const r = await api.post("/admin/marketing/broadcast/send", { kind, segment, dry_run });
      setLastResult({ kind, segment, dry_run, ...r });
      toast.success(dry_run ? `Dry run: ${r.total_matched} would receive` : `Sent to ${r.sent}, queued ${r.queued_for_retry}, skipped ${r.skipped_already_sent_24h}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setSending(null); }
  };

  const Card = ({ kind, title, sub, defaultSegment, badge }) => {
    const [segment, setSegment] = useState(defaultSegment);
    const aud = audiences[segment];
    const busy = sending === `${kind}:${segment}`;
    return (
      <div data-testid={`broadcast-${kind}`} className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="font-semibold">{title}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
          </div>
          {badge && <span className="text-[10px] rounded-full bg-primary/15 text-primary px-2 py-0.5 font-semibold shrink-0">{badge}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="text-muted-foreground">Send to:</span>
          {["trialing", "free", "trial_and_free"].map((s) => (
            <button key={s} onClick={() => setSegment(s)} data-testid={`broadcast-${kind}-seg-${s}`}
                    className={`rounded-full px-2 py-0.5 border ${segment === s ? "bg-primary/15 text-primary border-primary/40" : "bg-muted text-muted-foreground border-border"}`}>
              {s === "trial_and_free" ? "trial + free" : s} · {audiences[s]?.count ?? "…"}
            </button>
          ))}
        </div>
        {aud?.sample?.length > 0 && (
          <details className="text-[11px] text-muted-foreground">
            <summary className="cursor-pointer">Sample recipients</summary>
            <ul className="mt-1 space-y-0.5 ml-3">
              {aud.sample.map((u) => <li key={u.email}>{u.name || "—"} &lt;{u.email}&gt;</li>)}
            </ul>
          </details>
        )}
        <div className="flex gap-2">
          <button onClick={() => doSend(kind, segment, true)} disabled={busy}
                  data-testid={`broadcast-${kind}-dry`}
                  className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-40">
            {busy ? "…" : "Dry run"}
          </button>
          <button onClick={() => doSend(kind, segment, false)} disabled={busy || !aud?.count}
                  data-testid={`broadcast-${kind}-send`}
                  className="text-xs font-semibold rounded-full bg-primary text-primary-foreground px-3 py-1.5 hover:opacity-90 disabled:opacity-40">
            {busy ? "Sending…" : `Send to ${aud?.count ?? "?"}`}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="broadcasts-panel">
      <h3 className="font-semibold mb-1">One-off broadcasts</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Personal-tone messages you send yourself. Same recipient won&apos;t receive the same broadcast twice within 24h.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <Card kind="feedback_request"
              title="Founder feedback request"
              sub="Ask trialing users one open question. Every reply comes to your inbox."
              defaultSegment="trialing"
              badge="Highest signal" />
        <Card kind="price_announcement"
              title="New price announcement"
              sub="Tell free + trial users about the new $19/$89 pricing."
              defaultSegment="trial_and_free"
              badge="Conversion" />
      </div>
      {lastResult && (
        <div className="mt-3 text-xs rounded-lg bg-success/5 border border-success/30 p-2">
          Last result — <strong>{lastResult.kind}</strong> to <strong>{lastResult.segment}</strong>
          {lastResult.dry_run ? " (dry run)" : ""}:{" "}
          matched {lastResult.total_matched} · sent {lastResult.sent} · queued {lastResult.queued_for_retry} · skipped {lastResult.skipped_already_sent_24h}
        </div>
      )}
    </div>
  );
};


// -------------------------- WEBHOOK DIAGNOSTICS ---------------------------
const WebhookDiagnosticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get("/admin/marketing/webhook-diagnostics")); }
    catch { /* silent - non-critical */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const healthy = data.events_24h > 0 && (data.errors_24h === 0 || data.events_24h >= data.errors_24h);
  const lastEventAt = data.last_event?.at ? new Date(data.last_event.at).toLocaleString() : "never";
  const lastErrorAt = data.last_error?.at ? new Date(data.last_error.at).toLocaleString() : "—";

  return (
    <div data-testid="mkt-webhook-diagnostics" className={`rounded-xl border p-4 ${healthy ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Mail className={`w-4 h-4 ${healthy ? "text-success" : "text-warning"}`} />
            Resend webhook — open/click tracking
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Events flow: Resend → <code className="text-[10px]">{data.webhook_url}</code>
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="rounded-full">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Secret configured</div>
          <div className={`mt-1 font-bold ${data.secret_configured ? "text-success" : "text-destructive"}`}>
            {data.secret_configured ? "Yes" : "No"}
          </div>
        </div>
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Events 24h</div>
          <div className="mt-1 font-bold">{data.events_24h}</div>
        </div>
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Rejected 24h</div>
          <div className={`mt-1 font-bold ${data.errors_24h > 0 ? "text-destructive" : ""}`}>{data.errors_24h}</div>
        </div>
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Events total</div>
          <div className="mt-1 font-bold">{data.events_total}</div>
        </div>
      </div>

      <div className="mt-3 grid md:grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="font-semibold mb-1">Last accepted event</div>
          {data.last_event ? (
            <div className="text-muted-foreground">
              <div><span className="font-mono">{data.last_event.type}</span> — {lastEventAt}</div>
              {data.last_event.to && <div>to: {data.last_event.to}</div>}
            </div>
          ) : (
            <div className="text-muted-foreground">No events received yet.</div>
          )}
        </div>
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="font-semibold mb-1">Last rejection</div>
          {data.last_error ? (
            <div className="text-muted-foreground">
              <div><span className="font-mono">{data.last_error.reason}</span> — {lastErrorAt}</div>
              {data.last_error.detail && <div className="line-clamp-2">{data.last_error.detail}</div>}
            </div>
          ) : (
            <div className="text-muted-foreground">No rejections in the last 24h — signature is valid.</div>
          )}
        </div>
      </div>

      {data.error_reasons_24h?.length > 0 && (
        <div className="mt-3 text-xs">
          <div className="font-semibold mb-1">Rejection reasons (24h)</div>
          <div className="flex flex-wrap gap-2">
            {data.error_reasons_24h.map((r) => (
              <span key={r.reason} className="rounded-full bg-destructive/10 text-destructive px-2 py-0.5 font-mono">
                {r.reason} × {r.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {data.event_types_7d?.length > 0 && (
        <div className="mt-3 text-xs">
          <div className="font-semibold mb-1">Event types (7d)</div>
          <div className="flex flex-wrap gap-2">
            {data.event_types_7d.map((t) => (
              <span key={t.type} className="rounded-full bg-muted px-2 py-0.5 font-mono">
                {t.type} × {t.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {!healthy && data.errors_24h > 0 && data.events_24h === 0 && (
        <div className="mt-3 text-xs rounded-lg border border-destructive/40 bg-destructive/5 p-3">
          <strong>Every Resend event is being rejected.</strong> This is almost always a signing-secret mismatch.
          In Resend → Webhooks, open your endpoint, copy the <code>whsec_…</code> signing secret, and paste it into
          Admin → Settings & keys → <code>RESEND_WEBHOOK_SECRET</code>. Then click &quot;Send test&quot; in Resend and refresh here.
        </div>
      )}
    </div>
  );
};

// -------------------------- LAUNCH READINESS ------------------------------
const LaunchReadinessPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/marketing/readiness").then(setData).catch(() => {});
  }, []);
  if (!data) return null;

  const dot = (status) =>
    status === "ok" ? "bg-success" : status === "warn" ? "bg-warning" : "bg-destructive";

  return (
    <div data-testid="mkt-readiness-panel" className={`rounded-xl border p-4 ${data.ready ? "border-success/40 bg-success/5" : "border-destructive/40 bg-destructive/5"}`}>
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className={`w-5 h-5 ${data.ready ? "text-success" : "text-destructive"}`} />
        <h3 className="font-semibold" data-testid="mkt-readiness-title">
          {data.ready ? "Launch readiness: GO — flip a sequence on when you're ready" : "Launch readiness: blockers found"}
        </h3>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {data.items.map((it) => (
          <div key={it.key} data-testid={`mkt-readiness-${it.key}`} className="flex items-start gap-2 rounded-lg bg-card border border-border p-2.5">
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot(it.status)}`} />
            <div className="min-w-0">
              <div className="text-sm font-medium">{it.label}</div>
              <div className="text-xs text-muted-foreground">{it.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// -------------------------- PERFORMANCE -----------------------------------
const PerformancePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get("/admin/marketing/performance")); }
    catch { toast.error("Failed to load performance"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const ramp = data.ramp || {};
  const rampLabel = {
    grow: "Growing — deliverability healthy",
    hold_no_data: "Holding — not enough send data yet",
    hold_degraded: "Holding — metrics slightly degraded",
    throttle: "Throttled — deliverability degraded, protecting domain",
  }[ramp.last_action] || "Waiting for first daily pass";

  return (
    <div className="space-y-6">
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button></div>

      {/* Adaptive ramp */}
      <div data-testid="mkt-ramp-card" className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">Adaptive send limit (domain warm-up)</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          The system grows the daily cap while bounces &amp; complaints stay healthy, and throttles automatically when they don't.
        </p>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <div className="text-3xl font-bold" data-testid="mkt-ramp-cap">{ramp.current_cap}<span className="text-sm font-normal text-muted-foreground">/day</span></div>
            <div className="text-xs text-muted-foreground">of {ramp.max}/day max</div>
          </div>
          <div className="flex-1 min-w-[180px]">
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (ramp.current_cap / ramp.max) * 100)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">{rampLabel}</div>
          </div>
          {ramp.last_stats && (
            <div className="text-xs text-muted-foreground">
              7d: {ramp.last_stats.sent_7d} sent · bounce {(ramp.last_stats.bounce_rate * 100).toFixed(1)}% · complaints {(ramp.last_stats.complaint_rate * 100).toFixed(2)}%
            </div>
          )}
        </div>
      </div>

      {/* A/B tests */}
      <div>
        <h3 className="text-sm font-semibold mb-1 uppercase tracking-wider text-muted-foreground">Subject line A/B tests</h3>
        <p className="text-xs text-muted-foreground mb-3">Traffic splits 50/50; the winner locks in automatically at {data.ab_tests?.[0]?.min_sends_to_decide || 40}+ sends per variant.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {(data.ab_tests || []).map((t) => (
            <div key={t.template_key} data-testid={`mkt-ab-${t.template_key}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <code className="text-xs font-mono bg-secondary px-1.5 py-0.5 rounded">{t.template_key}</code>
                {t.status === "decided" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-success/20 text-success border border-success/30">Winner: {t.winner}</span>
                )}
                {t.status === "no_signal" && (
                  <span title={t.reason || "No engagement data — check webhook"} className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/30">No signal</span>
                )}
                {t.status === "running" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning border border-warning/30">Testing</span>
                )}
              </div>
              {["A", "B"].map((v) => {
                const st = t.stats?.[v] || { sends: 0, opens: 0, open_rate_pct: 0 };
                const isWinner = t.status === "decided" && t.winner === v;
                return (
                  <div key={v} className={`flex items-start gap-2 py-1.5 ${isWinner ? "" : t.status === "decided" ? "opacity-50" : ""}`}>
                    <span className={`text-xs font-bold mt-0.5 w-4 shrink-0 ${isWinner ? "text-success" : "text-muted-foreground"}`}>{v}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate" title={v === "A" ? t.subject_a : t.subject_b}>{v === "A" ? t.subject_a : t.subject_b}</div>
                      <div className="text-xs text-muted-foreground">{st.sends} sent · {st.opens} opens · {st.open_rate_pct}% open rate</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Per-step funnels */}
      <div>
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">Per-email performance</h3>
        <div className="space-y-4">
          {(data.sequences || []).map((s) => (
            <div key={s.id} data-testid={`mkt-perf-${s.id}`} className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 flex items-center gap-2 flex-wrap border-b border-border">
                <span className="font-semibold text-sm">{s.name}</span>
                {s.active
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-success/20 text-success border border-success/30">Active</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Paused</span>}
                <span className="ml-auto text-xs text-muted-foreground">{s.unsubscribes} unsubscribes</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Step</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-right px-3 py-2">Sends</th>
                    <th className="text-right px-3 py-2">Opens</th>
                    <th className="text-right px-3 py-2">Open %</th>
                    <th className="text-right px-3 py-2">Clicks</th>
                    <th className="text-right px-4 py-2">Click %</th>
                  </tr>
                </thead>
                <tbody>
                  {s.steps.map((st) => (
                    <tr key={st.step_num} className="border-t border-border">
                      <td className="px-4 py-2 text-muted-foreground">#{st.step_num}</td>
                      <td className="px-3 py-2"><code className="text-xs font-mono">{st.template_key}</code></td>
                      <td className="px-3 py-2 text-right">{st.sends}</td>
                      <td className="px-3 py-2 text-right">{st.opens}</td>
                      <td className={`px-3 py-2 text-right ${st.sends && st.open_rate_pct < 15 ? "text-destructive font-semibold" : ""}`}>{st.sends ? `${st.open_rate_pct}%` : "—"}</td>
                      <td className="px-3 py-2 text-right">{st.clicks}</td>
                      <td className="px-4 py-2 text-right">{st.sends ? `${st.click_rate_pct}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// -------------------------- SEQUENCES ------------------------------------
const SequencesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/marketing/sequences"); setRows(r.sequences || []); }
    catch { toast.error("Failed to load sequences"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row, next) => {
    if (next && !window.confirm(`Turn ON "${row.name}"? Emails will start flowing to matching contacts within a minute.`)) return;
    try {
      await api.patch(`/admin/marketing/sequences/${row.id}?active=${next}`, {});
      toast.success(next ? "Sequence active" : "Sequence paused");
      load();
    } catch { toast.error("Toggle failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3">
      {rows.map((s) => (
        <div key={s.id} data-testid={`mkt-seq-${s.id}`} className="rounded-xl border border-border bg-card p-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{s.name}</span>
              <code className="text-xs font-mono bg-secondary px-1.5 py-0.5 rounded">{s.id}</code>
              {s.active
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-success/20 text-success border border-success/30">Active</span>
                : <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Paused</span>
              }
            </div>
            <div className="text-xs text-muted-foreground mt-1">{s.description}</div>
            <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-3">
              <span>{s.steps.length} step{s.steps.length !== 1 ? "s" : ""}</span>
              <span>· <strong className="text-foreground">{s.enrollment_counts.active}</strong> active</span>
              <span>· {s.enrollment_counts.completed} completed</span>
              <span>· {s.enrollment_counts.exited} exited</span>
              <span>· {s.sent_count} sent</span>
            </div>
          </div>
          <button
            data-testid={`mkt-seq-toggle-${s.id}`}
            onClick={() => toggle(s, !s.active)}
            className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${
              s.active ? "bg-secondary text-foreground hover:bg-secondary/80" : "bg-primary text-primary-foreground hover:opacity-90"
            }`}
          >
            {s.active ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Turn on</>}
          </button>
        </div>
      ))}
    </div>
  );
};

// -------------------------- TEMPLATES ------------------------------------
const TemplatesPanel = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [versions, setVersions] = useState(null);
  const EMPTY = { subject: "", subject_b: "", preheader: "", body: "", button_text: "" };

  const loadList = useCallback(() => {
    api.get("/admin/marketing/templates").then((r) => {
      setItems(r.items || []);
      if (!selected && r.items?.length) setSelected(r.items[0].template_key);
    });
  }, [selected]);

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  const loadDetail = useCallback((key) => {
    api.get(`/admin/marketing/templates/detail/${key}`).then((d) => {
      setDetail(d);
      // Pre-fill editor with the effective copy (override if set, else code default).
      setDraft({ ...EMPTY, ...d.effective });
      setPreview(d.preview);
      setWarnings([]);
      setVersions(null);
    }).catch(() => toast.error("Failed to load template"));
  }, []);

  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  // Debounced live preview of the current (unsaved) draft.
  useEffect(() => {
    if (!selected || !draft) return;
    const t = setTimeout(() => {
      api.post(`/admin/marketing/templates/detail/${selected}/preview`, draft)
        .then((r) => { setPreview(r); setWarnings(r.warnings || []); })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [draft, selected]);

  const setField = (f, v) => setDraft((d) => ({ ...d, [f]: v }));

  const doSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/marketing/templates/detail/${selected}`, draft);
      setWarnings(r.warnings || []);
      toast.success((r.warnings?.length ? "Saved with warnings" : "Saved"));
      loadList(); loadDetail(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doTestSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/marketing/templates/detail/${selected}/test-send`, draft);
      toast.success(`Test sent to ${r.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setSending(false); }
  };

  const doReset = async () => {
    if (!window.confirm("Reset this template to the code default? Your saved edits will be removed (a version is kept in history).")) return;
    setResetting(true);
    try {
      await api.post(`/admin/marketing/templates/detail/${selected}/reset`);
      toast.success("Reset to default");
      loadList(); loadDetail(selected);
    } catch (e) { toast.error("Reset failed"); }
    finally { setResetting(false); }
  };

  const loadVersions = async () => {
    if (versions) { setVersions(null); return; }
    try { const r = await api.get(`/admin/marketing/templates/detail/${selected}/versions`); setVersions(r.versions || []); }
    catch { toast.error("Failed to load history"); }
  };

  const restore = async (updated_at) => {
    if (!window.confirm("Restore this version? It becomes the active override.")) return;
    try {
      await api.post(`/admin/marketing/templates/detail/${selected}/restore`, { updated_at });
      toast.success("Version restored");
      loadList(); loadDetail(selected); setVersions(null);
    } catch { toast.error("Restore failed"); }
  };

  const fieldRow = (label, f, opts = {}) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-muted-foreground">{label}</label>
        {opts.defaultText != null && (
          <span className="text-[10px] text-muted-foreground/70 truncate max-w-[55%]" title={opts.defaultText}>
            default: {opts.defaultText || "—"}
          </span>
        )}
      </div>
      {opts.textarea ? (
        <textarea
          data-testid={`tpl-field-${f}`}
          value={draft?.[f] ?? ""}
          onChange={(e) => setField(f, e.target.value)}
          rows={opts.rows || 8}
          className="form-input w-full font-mono text-xs"
        />
      ) : (
        <input
          data-testid={`tpl-field-${f}`}
          value={draft?.[f] ?? ""}
          onChange={(e) => setField(f, e.target.value)}
          className="form-input w-full"
        />
      )}
    </div>
  );

  return (
    <div className="grid md:grid-cols-[240px_1fr] gap-5">
      <aside className="space-y-1">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Templates</h3>
        <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-1">
          {items.map((t) => (
            <button
              key={t.template_key}
              data-testid={`mkt-tpl-${t.template_key}`}
              onClick={() => setSelected(t.template_key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2 ${selected === t.template_key ? "bg-primary/10 text-primary font-semibold" : "hover:bg-secondary"}`}
            >
              <span className="truncate">{t.template_key}</span>
              {t.has_override && <span data-testid={`tpl-edited-${t.template_key}`} className="shrink-0 text-[9px] uppercase font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">edited</span>}
            </button>
          ))}
        </div>
      </aside>

      {detail && draft && (
        <div className="grid lg:grid-cols-2 gap-5 min-w-0">
          {/* Editor */}
          <div className="space-y-3 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Pencil className="w-4 h-4 text-primary" />
              <code className="text-sm font-mono font-semibold">{selected}</code>
              {detail.has_override && <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">override active</span>}
            </div>

            {warnings.length > 0 && (
              <div data-testid="tpl-warnings" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
                {warnings.map((w, i) => (
                  <div key={i} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{w}</span></div>
                ))}
              </div>
            )}

            {fieldRow("Subject", "subject", { defaultText: detail.defaults.subject })}
            {fieldRow("A/B subject (optional)", "subject_b", { defaultText: detail.defaults.subject_b })}
            {fieldRow("Preheader", "preheader", { defaultText: detail.defaults.preheader })}
            {detail.defaults.has_button && fieldRow("Button text", "button_text", { defaultText: detail.defaults.button_text })}
            {fieldRow("Body (HTML)", "body", { textarea: true, rows: 12 })}

            <div className="rounded-lg bg-secondary/50 p-2.5 text-[11px] text-muted-foreground">
              <span className="font-semibold">Variables:</span>{" "}
              {detail.variables.map((v) => <code key={v.token} className="font-mono bg-background px-1 rounded mr-1">{v.token}</code>)}
              <span className="ml-1">— the CTA link &amp; unsubscribe footer are handled automatically.</span>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={doSave} disabled={saving} data-testid="tpl-save-btn" className="rounded-full">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
              </Button>
              <Button onClick={doTestSend} disabled={sending} variant="outline" data-testid="tpl-testsend-btn" className="rounded-full">
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send test to me
              </Button>
              <Button onClick={doReset} disabled={resetting || !detail.has_override} variant="outline" data-testid="tpl-reset-btn" className="rounded-full">
                {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset to default
              </Button>
              <Button onClick={loadVersions} variant="ghost" data-testid="tpl-history-btn" className="rounded-full">
                <History className="w-4 h-4 mr-1" />History
              </Button>
            </div>

            {versions && (
              <div data-testid="tpl-versions" className="rounded-xl border border-border divide-y divide-border">
                {versions.length === 0 && <div className="p-3 text-xs text-muted-foreground">No saved versions yet.</div>}
                {versions.map((v, i) => (
                  <div key={i} className="p-3 flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <div className="font-medium">{v.action === "reset" ? "Reset to default" : "Saved"} · {new Date(v.updated_at).toLocaleString()}</div>
                      <div className="text-muted-foreground truncate">{v.updated_by} — {v.fields?.subject || "(default subject)"}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => restore(v.updated_at)} data-testid={`tpl-restore-${i}`} className="rounded-full shrink-0">Restore</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="space-y-3 min-w-0">
            <div className="rounded-xl border border-border p-3 bg-card">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Subject</div>
              <div className="font-semibold text-sm mt-0.5" data-testid="tpl-preview-subject">{preview?.subject}</div>
              <div className="text-xs text-muted-foreground mt-1">Preheader: {preview?.preheader}</div>
            </div>
            <div className="rounded-xl border border-border bg-white overflow-hidden">
              <iframe
                title="Email preview"
                data-testid="tpl-preview-frame"
                srcDoc={preview?.html || ""}
                className="w-full"
                style={{ height: "640px", border: "none" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------- TRIAL EMAILS REVIEW ---------------------------
const TrialEmailsPanel = () => {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sendingKey, setSendingKey] = useState(null);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get("/admin/marketing/trial-emails")); }
    catch { toast.error("Failed to load trial emails"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (seq) => {
    const next = !seq.active;
    if (next && !window.confirm(`Activate ${seq.id}? Queued enrollments will start sending on the next scheduler tick.`)) return;
    setToggling(seq.id);
    try {
      await api.patch(`/admin/marketing/sequences/${seq.id}?active=${next}`);
      toast.success(next ? `${seq.id} is now LIVE` : `${seq.id} paused`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
    finally { setToggling(null); }
  };

  const testSend = async (templateKey) => {
    if (!testTo) { toast.error("Enter an email for test sends first"); return; }
    setSendingKey(templateKey);
    try {
      await api.post("/admin/marketing/test-send", { template_key: templateKey, to_email: testTo });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setSendingKey(null); }
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="mkt-trial-panel">
      <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-sm">Trial email review</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Every new signup is already enrolled - nothing sends while a sequence is paused. Review each email, send yourself tests, then activate.
          </div>
        </div>
        <input
          type="email"
          data-testid="mkt-trial-testto"
          placeholder="Send tests to this email"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          className="form-input min-w-[220px]"
        />
      </div>

      {data.sequences.map((seq) => (
        <div key={seq.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`mkt-trial-seq-${seq.id}`}>
          <div className="p-4 flex items-center gap-3 flex-wrap border-b border-border">
            <div className="flex-1 min-w-[240px]">
              <div className="font-semibold">{seq.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{seq.description}</div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${seq.active ? "bg-success/20 text-success border border-success/30" : "bg-secondary text-muted-foreground border border-border"}`}>
              {seq.active ? "LIVE" : "Paused"}
            </span>
            <Button
              data-testid={`mkt-trial-toggle-${seq.id}`}
              size="sm"
              variant={seq.active ? "outline" : "default"}
              className="rounded-full"
              onClick={() => toggle(seq)}
              disabled={toggling === seq.id}
            >
              {toggling === seq.id ? <Loader2 className="w-4 h-4 animate-spin" /> : seq.active ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activate</>}
            </Button>
          </div>
          <div className="divide-y divide-border">
            {seq.steps.map((st) => {
              const isOpen = openKey === st.template_key;
              return (
                <div key={st.template_key}>
                  <button
                    data-testid={`mkt-trial-step-${st.template_key}`}
                    onClick={() => setOpenKey(isOpen ? null : st.template_key)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-secondary/40 transition-colors"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-1 rounded-md whitespace-nowrap">
                      Day {st.day}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{st.subject}</div>
                      <div className="text-xs text-muted-foreground truncate">{st.preheader}</div>
                    </div>
                    <Eye className={`w-4 h-4 flex-shrink-0 ${isOpen ? "text-primary" : "text-muted-foreground"}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3">
                      <div className="rounded-lg border border-border bg-white overflow-hidden">
                        <iframe
                          title={st.template_key}
                          data-testid={`mkt-trial-preview-${st.template_key}`}
                          srcDoc={st.html}
                          className="w-full"
                          style={{ height: "540px", border: "none" }}
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid={`mkt-trial-send-${st.template_key}`}
                        onClick={() => testSend(st.template_key)}
                        disabled={sendingKey === st.template_key}
                      >
                        {sendingKey === st.template_key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        Send test{testTo ? ` to ${testTo}` : ""}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// -------------------------- COLD LEAD IMPORT ------------------------------
const ImportLeadsCard = ({ onDone }) => {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const loadStatus = useCallback(async () => {
    try { setStatus(await api.get("/admin/marketing/import-cold-leads/status")); } catch {}
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status?.state !== "running") return;
    const id = setInterval(loadStatus, 2000);
    return () => clearInterval(id);
  }, [status?.state, loadStatus]);
  useEffect(() => {
    if (status?.state === "done" && onDone) onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.state]);

  const start = async () => {
    if (!window.confirm("Import the bundled cold-lead CSV into cold_intro_v1? Idempotent — already-imported contacts are skipped. Nothing sends while the sequence is paused.")) return;
    setStarting(true);
    try {
      await api.post("/admin/marketing/import-cold-leads", {});
      toast.success("Import started");
      setTimeout(loadStatus, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed to start"); }
    finally { setStarting(false); }
  };

  return (
    <div data-testid="mkt-import-card" className="rounded-xl border border-border bg-card p-4 flex items-center gap-4 flex-wrap">
      <div className="flex-1 min-w-[240px]">
        <div className="font-semibold text-sm">Cold lead import (bundled CSV)</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {status?.state === "running" && `Running… ${status.processed || 0}/${status.total || "?"} processed · ${status.enrolled || 0} newly enrolled`}
          {status?.state === "done" && `Last import: ${status.imported} contacts, ${status.enrolled} newly enrolled into cold_intro_v1`}
          {status?.state === "error" && <span className="text-destructive">Error: {status.error}</span>}
          {(!status || status.state === "idle") && "One click imports /app/leads/combined_clean.csv and enrolls into cold_intro_v1 (paused). Safe to re-run."}
        </div>
      </div>
      <Button
        data-testid="mkt-import-btn"
        size="sm"
        onClick={start}
        disabled={starting || status?.state === "running"}
        className="rounded-full"
      >
        {status?.state === "running" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
        {status?.state === "done" ? "Re-run import" : "Import cold leads"}
      </Button>
    </div>
  );
};

// -------------------------- MOCK EVENT ------------------------------------
const MockEventPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState({ total: 0, reservations: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [info, res] = await Promise.all([
        api.get("/mock-event/info"),
        api.get("/admin/mock-event/reservations"),
      ]);
      setCfg(info);
      setRows(res);
    } catch { toast.error("Failed to load mock event"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patch("/admin/mock-event", {
        title: cfg.title, subtitle: cfg.subtitle, event_at: cfg.event_at, active: cfg.active,
      });
      setCfg(updated);
      toast.success("Event updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5">
      <div data-testid="mkt-event-config" className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Alpha Mock event — public page at <code className="text-xs font-mono bg-secondary px-1.5 py-0.5 rounded">/mock-event</code></h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.active ? "bg-success/20 text-success border border-success/30" : "bg-secondary text-muted-foreground"}`}>
            {cfg.active ? "Reservations open" : "Closed"}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Title</label>
            <input data-testid="mkt-event-title" className="form-input w-full mt-1" value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Event date/time (ISO, Sydney offset)</label>
            <input data-testid="mkt-event-date" className="form-input w-full mt-1 font-mono text-sm" value={cfg.event_at} onChange={(e) => setCfg({ ...cfg, event_at: e.target.value })} placeholder="2026-09-12T10:00:00+10:00" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Subtitle</label>
          <input data-testid="mkt-event-subtitle" className="form-input w-full mt-1" value={cfg.subtitle} onChange={(e) => setCfg({ ...cfg, subtitle: e.target.value })} />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="mkt-event-active" type="checkbox" checked={!!cfg.active} onChange={(e) => setCfg({ ...cfg, active: e.target.checked })} />
            Reservations open
          </label>
          <Button data-testid="mkt-event-save" size="sm" onClick={save} disabled={saving} className="rounded-full ml-auto">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">
          Reservations ({rows.total})
        </h3>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Reserved at</th>
              </tr>
            </thead>
            <tbody>
              {rows.reservations.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No reservations yet — share /mock-event to start filling seats.</td></tr>
              )}
              {rows.reservations.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.name || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// -------------------------- CONTACTS -------------------------------------
const ContactsPanel = () => {
  const [q, setQ] = useState("");
  const [warmth, setWarmth] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ contacts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (warmth) params.set("warmth", warmth);
      if (status) params.set("status", status);
      params.set("limit", LIMIT);
      params.set("offset", offset);
      setData(await api.get(`/admin/marketing/contacts?${params}`));
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  }, [q, warmth, status, offset]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <ImportLeadsCard onDone={load} />
      <div className="flex gap-2 flex-wrap items-center">
        <input data-testid="mkt-contacts-search" placeholder="Search email..." value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} className="form-input min-w-[220px]" />
        <select data-testid="mkt-contacts-warmth" value={warmth} onChange={(e) => { setOffset(0); setWarmth(e.target.value); }} className="form-input">
          <option value="">All warmth</option>
          <option value="cold">Cold</option>
          <option value="lead">Lead</option>
          <option value="member">Member</option>
          <option value="pro">Pro</option>
          <option value="churned">Churned</option>
        </select>
        <select data-testid="mkt-contacts-status" value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }} className="form-input">
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
          <option value="complained">Complained</option>
        </select>
        <div className="ml-auto text-sm text-muted-foreground">
          {data.total} total
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Warmth</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Sends</th>
                  <th className="text-right px-3 py-2">Opens</th>
                  <th className="text-right px-3 py-2">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((c) => (
                  <tr key={c.id} data-testid={`mkt-contact-row-${c.id}`} className="border-t border-border">
                    <td className="px-3 py-2">{c.email}</td>
                    <td className="px-3 py-2 text-muted-foreground">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-2">{c.warmth}</td>
                    <td className="px-3 py-2">{c.status}</td>
                    <td className="px-3 py-2 text-right">{c.total_sends || 0}</td>
                    <td className="px-3 py-2 text-right">{c.total_opens || 0}</td>
                    <td className="px-3 py-2 text-right">{c.total_clicks || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button data-testid="mkt-contacts-prev" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} className="px-3 py-1.5 rounded-lg border border-border text-sm disabled:opacity-50 inline-flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Prev</button>
            <span className="text-xs text-muted-foreground">{offset + 1}–{Math.min(offset + LIMIT, data.total)} of {data.total}</span>
            <button data-testid="mkt-contacts-next" disabled={offset + LIMIT >= data.total} onClick={() => setOffset(offset + LIMIT)} className="px-3 py-1.5 rounded-lg border border-border text-sm disabled:opacity-50 inline-flex items-center gap-1">Next <ChevronRight className="w-3 h-3" /></button>
          </div>
        </>
      )}
    </div>
  );
};

// -------------------------- WORKER STALE BANNER --------------------------
const WorkerStaleBanner = () => {
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/marketing/worker-health").then((d) => { if (alive) setH(d); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!h || !h.stale) return null;
  const hrs = h.seconds_since_success != null ? Math.round(h.seconds_since_success / 3600) : null;
  return (
    <div data-testid="mkt-worker-stale-banner" className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
      <div className="text-sm">
        <div className="font-semibold text-destructive">Email worker looks stale</div>
        <div className="text-muted-foreground">
          No successful scheduler run {hrs != null ? `for ~${hrs}h` : "recorded"} (alerts after {h.stale_after_hours}h).
          {h.last_error ? ` Last error: ${h.last_error}` : ""} Sequence sends may be paused.
        </div>
      </div>
    </div>
  );
};

// -------------------------- SEQUENCE LOG ---------------------------------
const SequenceLogPanel = () => {
  const [counts, setCounts] = useState(null);
  const [worker, setWorker] = useState(null);
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [tally, setTally] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [f, setF] = useState({ sequence_id: "", status: "", source: "", q: "" });

  const loadCounts = useCallback(async () => {
    try { const d = await api.get("/admin/marketing/sequence-counts"); setCounts(d.sequences || []); setWorker(d.worker || null); }
    catch { toast.error("Failed to load sequence counts"); }
  }, []);
  const loadPlan = useCallback(async () => {
    try { setPlan(await api.get("/admin/marketing/catchup/plan?sample=50")); } catch {}
  }, []);
  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
      const d = await api.get(`/admin/marketing/sequence-log?limit=100${qs ? `&${qs}` : ""}`);
      setRows(d.rows || []); setTally(d.tally || {}); setTotal(d.total || 0);
    } catch { toast.error("Failed to load send log"); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { loadCounts(); loadPlan(); }, [loadCounts, loadPlan]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const dryRun = async () => {
    setBusy("dryrun");
    try { await api.post("/admin/marketing/catchup/dry-run?grace_hours=6&throttle_minutes=8", {}); toast.success("Dry-run plan built"); await loadPlan(); }
    catch { toast.error("Dry-run failed"); }
    finally { setBusy(""); }
  };
  const approve = async () => {
    if (!window.confirm("Approve the catch-up plan? The worker will begin sending the planned touches, throttled and gap-checked. Skipped/stale touches will not send.")) return;
    setBusy("approve");
    try { await api.post("/admin/marketing/catchup/approve", {}); toast.success("Catch-up approved — sending will begin"); await loadPlan(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(""); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try { await api.post("/admin/marketing/catchup/cancel", {}); toast.success("Plan cancelled"); await loadPlan(); }
    catch { toast.error("Cancel failed"); }
    finally { setBusy(""); }
  };

  const StatusPill = ({ s }) => {
    const map = { sent: "bg-green-500/15 text-green-600", failed: "bg-destructive/15 text-destructive", skipped: "bg-amber-500/15 text-amber-600" };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || "bg-muted"}`}>{s}</span>;
  };
  const fmt = (t) => (t ? new Date(t).toLocaleString() : "—");

  return (
    <div className="space-y-6" data-testid="mkt-sendlog-panel">
      {/* Worker health */}
      {worker && (
        <div className="rounded-xl border border-border p-4 bg-card">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Worker health</h3>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { loadCounts(); loadLog(); loadPlan(); }}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Status</div><div className={`font-semibold ${worker.stale ? "text-destructive" : "text-green-600"}`}>{worker.stale ? "Stale" : "Running"}</div></div>
            <div><div className="text-xs text-muted-foreground">Last successful run</div><div className="font-medium">{fmt(worker.last_success_at)}</div></div>
            <div><div className="text-xs text-muted-foreground">Last tick</div><div className="font-medium">sent {worker.last_tick?.sent ?? 0} · fail {worker.last_tick?.failed ?? 0} · skip {worker.last_tick?.skipped ?? 0}</div></div>
            <div><div className="text-xs text-muted-foreground">Backlog (awaiting catch-up)</div><div className="font-medium">{worker.backlog_count ?? 0}</div></div>
            <div><div className="text-xs text-muted-foreground">Last error</div><div className="font-medium truncate" title={worker.last_error || ""}>{worker.last_error || "none"}</div></div>
          </div>
        </div>
      )}

      {/* Per-sequence counts */}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground"><tr>
            {["Sequence", "On", "Enrolled", "Active", "Completed", "Exited", "Sent", "Failed", "Skipped"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {(counts || []).map((s) => (
              <tr key={s.id} data-testid={`mkt-seqcount-${s.id}`} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2">{s.active ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2">{s.enrolled}</td>
                <td className="px-3 py-2">{s.active_enroll}</td>
                <td className="px-3 py-2">{s.completed}</td>
                <td className="px-3 py-2">{s.exited}</td>
                <td className="px-3 py-2 text-green-600">{s.sent}</td>
                <td className="px-3 py-2 text-destructive">{s.failed}</td>
                <td className="px-3 py-2 text-amber-600">{s.skipped}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Catch-up */}
      <div className="rounded-xl border border-border p-4 bg-card space-y-3" data-testid="mkt-catchup-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Catch-up (backlog)</h3>
            <p className="text-xs text-muted-foreground mt-1">Sends only the next appropriate touch per person, throttled, with a 20-hour gap. Nothing sends until you approve.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" disabled={busy === "dryrun"} onClick={dryRun} data-testid="mkt-catchup-dryrun">{busy === "dryrun" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Build dry-run</Button>
            {plan && ["pending", "approved", "running"].includes(plan.status) && (
              <>
                {plan.status === "pending" && <Button size="sm" className="rounded-full" disabled={busy === "approve"} onClick={approve} data-testid="mkt-catchup-approve">{busy === "approve" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Approve &amp; send</Button>}
                <Button size="sm" variant="outline" className="rounded-full" disabled={busy === "cancel"} onClick={cancel} data-testid="mkt-catchup-cancel">Cancel</Button>
              </>
            )}
          </div>
        </div>
        {plan && plan.status !== "none" ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-2 py-1 rounded-lg bg-muted">Status: <strong>{plan.status}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-green-500/10">Will send: <strong>{plan.counts?.send ?? 0}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-amber-500/10">Skip (stale): <strong>{plan.counts?.skip_stale ?? 0}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-muted">Exit: <strong>{plan.counts?.exit ?? 0}</strong></span>
              {plan.progress && <span className="px-2 py-1 rounded-lg bg-muted">Progress: sent {plan.progress.sent} · remaining {plan.progress.remaining}</span>}
            </div>
            <div className="overflow-x-auto max-h-80 border border-border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground sticky top-0"><tr>
                  {["Email", "Sequence", "Action", "Step", "Reason", "Scheduled", "State"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(plan.items || []).map((i) => (
                    <tr key={i.id} className="border-t border-border">
                      <td className="px-2 py-1.5">{i.email}</td>
                      <td className="px-2 py-1.5">{i.sequence_id}</td>
                      <td className="px-2 py-1.5">{i.action}</td>
                      <td className="px-2 py-1.5">{i.step_num ?? "—"}</td>
                      <td className="px-2 py-1.5">{i.reason}</td>
                      <td className="px-2 py-1.5">{i.scheduled_for ? fmt(i.scheduled_for) : "—"}</td>
                      <td className="px-2 py-1.5">{i.state}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">Showing up to 50 planned rows. Full plan: {plan.counts?.total ?? 0} enrolments reviewed.</p>
          </div>
        ) : <p className="text-sm text-muted-foreground">No catch-up plan yet. Build a dry-run to preview who would get which touch.</p>}
      </div>

      {/* Send log */}
      <div className="rounded-xl border border-border p-4 bg-card space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Send log</h3>
          <div className="flex gap-3 text-xs">
            <span className="text-green-600">sent {tally.sent ?? 0}</span>
            <span className="text-destructive">failed {tally.failed ?? 0}</span>
            <span className="text-amber-600">skipped {tally.skipped ?? 0}</span>
            <span className="text-muted-foreground">({total} matching)</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select data-testid="mkt-log-filter-seq" value={f.sequence_id} onChange={(e) => setF({ ...f, sequence_id: e.target.value })} className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm">
            <option value="">All sequences</option>
            {(counts || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="mkt-log-filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm">
            {["", "sent", "failed", "skipped"].map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <select data-testid="mkt-log-filter-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm">
            {["", "worker", "catchup"].map((s) => <option key={s} value={s}>{s || "All sources"}</option>)}
          </select>
          <input data-testid="mkt-log-filter-email" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Email contains…" className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm" />
        </div>
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground"><tr>
              {["When", "Email", "Sequence", "Step", "Template", "Source", "Status", "Provider ID / error"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="px-3 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></td></tr>
                : rows.length === 0 ? <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No log entries yet.</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} data-testid="mkt-log-row" className="border-t border-border">
                    <td className="px-2 py-1.5 whitespace-nowrap">{fmt(r.attempted_at)}</td>
                    <td className="px-2 py-1.5">{r.contact_email}</td>
                    <td className="px-2 py-1.5">{r.sequence_id}</td>
                    <td className="px-2 py-1.5">{r.step_num ?? "—"}</td>
                    <td className="px-2 py-1.5">{r.template_key || "—"}</td>
                    <td className="px-2 py-1.5">{r.source}</td>
                    <td className="px-2 py-1.5"><StatusPill s={r.status} /></td>
                    <td className="px-2 py-1.5 max-w-[220px] truncate" title={r.error || r.provider_message_id || ""}>{r.error || r.provider_message_id || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default AdminMarketingPage;
